import { motion } from "framer-motion";
import { Video, Shield, Smartphone, Users, Clock, FileText, Heart, Monitor } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";

const Telemedicine = () => {
  const features = [
    { icon: Video, title: "HD video consultations with healthcare providers", description: "Crystal clear video quality for accurate diagnosis", color: "text-secondary" },
    { icon: Shield, title: "Secure messaging and file sharing", description: "HIPAA-compliant communication platform", color: "text-accent-teal" },
    { icon: FileText, title: "Digital prescription management", description: "Electronic prescriptions sent directly to pharmacy", color: "text-primary" },
    { icon: Monitor, title: "Electronic health record integration", description: "Seamless integration with existing EHR systems", color: "text-secondary" },
    { icon: Smartphone, title: "Multi-device compatibility", description: "Access from any device, anywhere, anytime", color: "text-accent-teal" },
    { icon: Shield, title: "HIPAA-compliant security", description: "Bank-level encryption and security protocols", color: "text-primary" }
  ];

  const benefits = [
    { title: "Reduced Travel Time", percentage: 85, description: "Average time saved per consultation", color: "bg-secondary" },
    { title: "Faster Consultations", percentage: 70, description: "Quicker access to healthcare specialists", color: "bg-accent-teal" },
    { title: "Cost Reduction", percentage: 60, description: "Lower healthcare delivery costs", color: "bg-primary" },
    { title: "Patient Satisfaction", percentage: 95, description: "Patients satisfied with telemedicine experience", color: "bg-secondary" }
  ];

  const stats = [
    { value: "1M+", label: "Consultations Completed", icon: Video },
    { value: "24/7", label: "Availability", icon: Clock },
    { value: "99.9%", label: "Uptime Reliability", icon: Shield },
    { value: "50+", label: "Medical Specialties", icon: Users }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      <main className="pt-32 pb-16 relative overflow-hidden">
        
        {/* Animated telemedicine icons */}
        <motion.div
          className="absolute top-1/4 right-1/4 w-16 h-16 text-secondary/10"
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 10, 0]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Video className="w-full h-full" />
        </motion.div>
        
        <div className="container-padding relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Hero Section */}
            <motion.div 
              className="text-center mb-20"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 
                           border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium
                           backdrop-blur-sm mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Video className="w-4 h-4" />
                TELEMEDICINE SOLUTIONS
              </motion.div>
              
              <motion.h1 
                className="heading-xl mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Virtual Healthcare Excellence
              </motion.h1>
              
              <motion.p 
                className="text-xl text-neutral-700 max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Advanced telehealth solutions connecting patients with healthcare professionals 
                through secure, high-quality virtual consultations that deliver care anywhere, anytime.
              </motion.p>
            </motion.div>

            {/* Stats Section */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="healthcare-card text-center group"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-white to-neutral-50 text-secondary mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <stat.icon className="w-8 h-8" />
                  </div>
                  <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-neutral-600 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Main Content */}
            <div className="grid lg:grid-cols-2 gap-16 mb-20">
              {/* Platform Features */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-secondary to-accent-teal text-white">
                    <Smartphone className="w-8 h-8" />
                  </div>
                  <h2 className="text-3xl font-bold text-primary">Platform Features</h2>
                </div>
                
                <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                  Our telemedicine platform enables seamless healthcare delivery regardless of location, 
                  providing access to medical expertise when and where it's needed most.
                </p>
                
                <div className="space-y-4">
                  {features.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      className="healthcare-card group"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                      whileHover={{ x: 5, scale: 1.02 }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-neutral-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative z-10 flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-white to-neutral-50 ${feature.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <feature.icon className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-primary mb-2">{feature.title}</h3>
                          <p className="text-neutral-600 text-sm">{feature.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-accent-teal to-primary text-white">
                    <Heart className="w-8 h-8" />
                  </div>
                  <h2 className="text-3xl font-bold text-primary">Healthcare Benefits</h2>
                </div>
                
                <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                  Reduce travel time, increase access to specialists, enable faster consultations, 
                  and provide continuous care monitoring - all while maintaining the highest standards 
                  of medical care and patient privacy.
                </p>

                {/* Benefits Progress Bars */}
                <div className="space-y-8">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit.title}
                      className="healthcare-card group"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-neutral-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative z-10">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-bold text-primary">{benefit.title}</h3>
                          <span className="text-2xl font-bold text-secondary">{benefit.percentage}%</span>
                        </div>
                        <div className="w-full bg-neutral-200 rounded-full h-3 mb-2">
                          <motion.div 
                            className={`h-3 rounded-full ${benefit.color}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${benefit.percentage}%` }}
                            transition={{ duration: 1.5, delay: 1.0 + index * 0.2 }}
                          />
                        </div>
                        <p className="text-neutral-600 text-sm">{benefit.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Remote Healthcare Access Section */}
            <motion.div
              className="healthcare-card group mb-20"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent-teal/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 text-center">
                <div className="flex items-center justify-center gap-4 mb-8">
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-secondary to-accent-teal text-white">
                    <Monitor className="w-10 h-10" />
                  </div>
                  <h2 className="text-4xl font-bold text-primary">Remote Healthcare Access</h2>
                </div>
                <p className="text-xl text-neutral-700 leading-relaxed max-w-4xl mx-auto">
                  Breaking down geographical barriers to healthcare, our platform ensures that quality 
                  medical care is accessible to everyone, from urban centers to remote locations, 
                  providing life-saving consultations when traditional healthcare isn't available.
                </p>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <motion.button 
                className="button-primary group mr-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Video className="w-5 h-5 mr-2" />
                Start Telemedicine
              </motion.button>
              <motion.button 
                className="button-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Request Demo
              </motion.button>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Telemedicine;